import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { Button } from '@fyos/components';
import { notificationApi, Notification } from '@/lib/notificationApi';
import { useToastStore } from '@/stores/toastStore';
import Skeleton from '@/components/Skeleton';

interface NotificationBellProps {
  workspaceId?: string;
}

export default function NotificationBell({ workspaceId }: NotificationBellProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const initializedRef = useRef(false);
  const knownNotificationIdsRef = useRef<Set<string>>(new Set());
  const addToast = useToastStore((s) => s.addToast);
  const navigate = useNavigate();

  const getToastType = (notifType: string) => {
    switch (notifType) {
      case 'mention': return 'mention' as const;
      case 'auto_kill_warning': return 'warning' as const;
      case 'auto_kill_executed': return 'error' as const;
      default: return 'info' as const;
    }
  };

  const pollNotifications = useCallback(async () => {
    try {
      const latest = await notificationApi.getAll(undefined, true);
      setUnreadCount(latest.length);

      if (!initializedRef.current) {
        // First poll — seed known IDs, no toasts
        latest.forEach((n) => knownNotificationIdsRef.current.add(n.id));
        initializedRef.current = true;
      } else {
        // Subsequent polls — show toasts for new notifications
        const newOnes = latest.filter((n) => !knownNotificationIdsRef.current.has(n.id));
        latest.forEach((n) => knownNotificationIdsRef.current.add(n.id));

        for (const n of newOnes) {
          const notifId = n.id;
          const data = typeof n.data === 'string' ? JSON.parse(n.data) : n.data;
          const itemId = data?.item_id;
          const wsId = n.workspace_id;

          addToast({
            type: getToastType(n.type),
            title: n.title,
            message: n.message,
            icon: n.type === 'mention' ? '💬' : undefined,
            duration: 8000,
            action: itemId && wsId ? {
              label: 'View',
              onClick: () => {
                notificationApi.markAsRead(notifId).catch(() => {});
                setUnreadCount((prev) => Math.max(0, prev - 1));
                navigate(`/roadmap/${wsId}/item/${itemId}`);
              },
            } : undefined,
          });
        }
      }
    } catch (err) {
      console.error('Failed to poll notifications:', err);
    }
  }, [addToast, navigate]);

  useEffect(() => {
    pollNotifications();
    const interval = setInterval(pollNotifications, 15000);
    return () => clearInterval(interval);
  }, [pollNotifications]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationApi.getAll(workspaceId, true);
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!showPanel) {
      fetchNotifications();
    }
    setShowPanel(!showPanel);
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications(notifications.filter(n => n.id !== id));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead(workspaceId);
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'mention':
        return '💬';
      case 'auto_kill_warning':
        return '⚠️';
      case 'auto_kill_executed':
        return '🔴';
      default:
        return '🔔';
    }
  };

  const getTimeAgo = (dateString: string) => {
    // Server sends UTC timestamps without Z suffix — ensure JS parses as UTC
    const normalized = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString.replace(' ', 'T') + 'Z';
    const date = new Date(normalized);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <>
      <button
        onClick={handleBellClick}
        className="relative inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors overflow-visible"
        title="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 z-10 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-[10px] font-bold leading-none text-white bg-red-600 rounded-full shadow-sm">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowPanel(false)} />
          <div className="relative z-50 w-full max-w-2xl bg-white dark:bg-gray-800 shadow-xl flex flex-col animate-in slide-in-from-right">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h2>
                {unreadCount > 0 && (
                  <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center font-bold">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
                    Mark all read
                  </Button>
                )}
                <button onClick={() => setShowPanel(false)} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Notification list */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="p-4">
                      <div className="flex items-start space-x-3">
                        <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                        <div className="flex-1 min-w-0 space-y-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-3 w-full" />
                          <div className="flex items-center justify-between">
                            <Skeleton className="h-3 w-16" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
                  <Bell className="h-12 w-12 mb-3 opacity-30" />
                  <p className="text-lg font-medium">No new notifications</p>
                  <p className="text-sm mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {notifications.map(notification => (
                    <div
                      key={notification.id}
                      className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl flex-shrink-0">
                          {getNotificationIcon(notification.type)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              {getTimeAgo(notification.created_at)}
                            </span>
                            <button
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="text-xs text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300"
                            >
                              Mark read
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
